import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Typography, TextField } from "@mui/material";
import TeachingModal from "../Modal/TeachingModal";
import { currentAssetSliceActions } from "../../Store/Slices/CurrentAssetSlice";
import { commonSliceActions } from "../../Store/Slices/CommonSlice";
import Confirmation from "../Modal/Confirmation";

/**
 * Recursive MUI TreeView component that supports arbitrary depth (n levels).
 *
 * Props
 * - nodes: Array of node objects { id: string|number, label: string, children?: [] }
 * - defaultExpanded: array of node ids to expand by default
 * - onNodeClick: function(nodeId)
 */
const TeachTreeView = ({
  nodes = [
    // {
    //   id: 1,
    //   label: "Root",
    //   children: [
    //     { id: 2, label: "Child A", children: [{ id: 3, label: "Grandchild" }] },
    //     { id: 4, label: "Child B" },
    //   ],
    // },
  ],
  defaultExpanded = [],
  onNodeClick,
}) => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded.map(String));
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [classModal, setClassModal] = useState(false);

  const [product, setProduct] = useState({});
  const [alertFlag, setAlertFlag] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [module, setModule] = useState([]);
  const [currentPathObj, setCurrentPathObj] = useState({});

  let coursePermissions = useSelector(
    (state) => state.defs.siteConfig.products[0]["coursePermissions"]
  );
  let { productData, language } = useSelector((state) => state.product);
  const userInfo = useSelector((state) => state.loggedInUserInfo.userInfo);

  useEffect(() => {
    console.log("productData", productData);
    dispatch(
      commonSliceActions.setBreadcrumb(null)
    );
    let data = productData;
    if (data) {
      if (typeof data == "string") {
        data = JSON.parse(data);
      }
      let courseObj;
      if (typeof data[0]["course_data"] == "string") {
        courseObj = JSON.parse(data[0]["course_data"]);
      } else {
        courseObj = product[0]["course_data"];
      }
      setProduct(courseObj["data"][0]["data"]);
      console.log("IIII", courseObj["data"][0]["data"]);
      if (!courseObj.data[0]?.data[0]?.data[0]?.data[0]?.data[0].isClass) {
        if (language == -1) {
          setModule(courseObj["data"][0]["data"][0]["data"]);
          setLanguages(courseObj["data"][0]["data"][0]["value"]);
        } else {
          setModule(courseObj["data"][0]["data"][language]["data"]);
          setLanguages(courseObj["data"][0]["data"][language]["value"]);
        }
      } else {
        setModule(courseObj["data"][0]["data"]);
      }
      setIsLoading(false);
    } else {
      // if (location.pathname.includes("CourseConfig")) {
      //   dispatch(commonSliceActions.setLoader(false));
      // }
    }
  }, [productData, coursePermissions, language]);

  useEffect(() => {
    console.log("module", module);
  }, [module]);

  const handleToggle = useCallback((event, nodeIds) => {
    setExpanded(nodeIds);
  }, []);

  const handleSelect = (event, nodeId) => {
    setSelected(nodeId);
    const pathObj = findNodePath(module, nodeId);
    if (pathObj?.isClass) {
      if (pathObj?.data?.length > 0) {
        setCurrentPathObj(pathObj);
        if (userInfo.rolesInfo.title.toLowerCase() == "trainer") {
          setConfirmModal(true);
        } else {
          redirectToAssets(pathObj);
        }
      } else {
        setAlertFlag(true);
        return false;
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter tree nodes recursively based on search query
  const filterNodes = useCallback((nodes, query) => {
    if (!query) return nodes;
    return nodes
      .map((node) => {
        if (node.value.toLowerCase().includes(query.toLowerCase())) {
          return { ...node };
        }
        const data = node.data ? filterNodes(node.data, query) : [];
        if (data.length > 0) {
          return { ...node, data };
        }
        return null;
      })
      .filter(Boolean);
  }, []);

  const filteredNodes = useMemo(
    () => filterNodes(module, searchQuery),
    [module, searchQuery, filterNodes]
  );

  // Find full path from top node to target nodeId
  const findNodePath = (
    nodes,
    targetId,
    path = [],
    breadcrumb = [languages]
  ) => {
    for (let node of nodes) {
      const currentPath = [...path, node.id];
      const currentBreadcrumb = [...breadcrumb, node.value];
      if (String(node.id) === String(targetId)) {
        return {
          path: currentPath,
          isClass: node.isClass,
          data: node.data,
          breadcrumb: currentBreadcrumb,
        };
      }
      if (node.data) {
        const childPath = findNodePath(
          node.data,
          targetId,
          currentPath,
          currentBreadcrumb
        );
        if (childPath) return childPath;
      }
    }
    return null;
  };

  const redirectToAssets = (pathObj = currentPathObj) => {
    setConfirmModal(false);
    dispatch(
      commonSliceActions.setBreadcrumb(`${pathObj.breadcrumb.join(" | ")}`)
    );
    navigation(`/Teach/Content/${pathObj.path.join("/")}`);
  };

  // Render a single TreeItem (recursive)
  const renderTree = (node) => {
    const nodeId = String(node.id);

    const label = (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "primary.main",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "500" }}>
          {node.value}
        </Typography>
      </Box>
    );

    return (
      <TreeItem
        key={nodeId}
        nodeId={nodeId}
        label={label}
        sx={{ paddingY: 0.8 }}
      >
        {Array.isArray(node.data) &&
        node.data.length > 0 &&
        node.isClass === false
          ? node.data.map((child) => renderTree(child))
          : null}
      </TreeItem>
    );
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{ mb: 1 }}
      />
      <TreeView
        aria-label="recursive-tree"
        defaultCollapseIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        defaultExpandIcon={<ChevronRightIcon sx={{ color: "primary.main" }} />}
        expanded={expanded}
        selected={selected}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
        multiSelect={false}
        sx={{ width: "75vw", height: "75vh", overflow: "auto", padding: 2 }}
      >
        {filteredNodes.map((n) => renderTree(n))}
      </TreeView>
      <Confirmation
        visible={alertFlag}
        primaryText={"Assets not available"}
        image="error"
        alert={true}
        toggleModal={(flag) => setAlertFlag(flag)}
        timeout={2000}
      ></Confirmation>
      <Confirmation
        image="assign"
        primaryText="content for ?"
        visible={confirmModal}
        toggleModal={(flag) => setConfirmModal(flag)}
        positiveClick={() => {
          setConfirmModal(false);
          setClassModal(true);
        }}
        negativeClick={() => {
          dispatch(
            currentAssetSliceActions.setTrainerContinueTeachingClass("self")
          );
          dispatch(
            currentAssetSliceActions.setTrainerContinueTeachingClassName("Self")
          );
          redirectToAssets();
        }}
        positiveText="Learning"
        negativeText="Review"
      ></Confirmation>
      <TeachingModal
        visible={classModal}
        toggleModal={(flag) => setClassModal(flag)}
        go={(selectedclass, selectedclassname) => {
          dispatch(
            currentAssetSliceActions.setTrainerContinueTeachingClass(
              selectedclass
            )
          );
          dispatch(
            currentAssetSliceActions.setTrainerContinueTeachingClassName(
              selectedclassname
            )
          );
          redirectToAssets();
        }}
      ></TeachingModal>
    </>
  );
};

TeachTreeView.propTypes = {
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.node.isRequired,
      data: PropTypes.array,
    })
  ),
  defaultExpanded: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onNodeClick: PropTypes.func,
};

/**
 * Example `nodes` shape you can pass in:
 *
 * const nodes = [
 *   { id: 1, label: 'Root', children: [
 *     { id: 2, label: 'Child A', children: [ { id: 3, label: 'Grandchild' } ] },
 *     { id: 4, label: 'Child B' }
 *   ] }
 * ];
 */
export default TeachTreeView;
